import {
  GQIngestionCustomFieldType,
  GQTaskWatershedProcessStateSimplified,
} from '@watershed/app-dashboard/generated/graphql-schema-types';
import { PermissionType } from '@watershed/constants/permissions';

import {
  GQMeasurementProjectPageIngestionCustomFieldFragment,
  GQPresetCustomTagForMeasurementPageFragment,
} from '@watershed/app-dashboard/generated/graphql-operations';

import {
  MeasurementFlattenedTask,
  MeasurementTask,
} from '../measurementTaskTypes';
import { useCallback, useMemo } from 'react';
import { Analytics } from '@watershed/analytics/analyticsUtils';
import useHasPermission from '../../../utils/useHasPermission';
import { useUserContext } from '../../../utils/UserContext';
import { useQueryParam } from '@watershed/shared-frontend/utils/queryParamHooks';
import { TASK_WATERSHED_PROCESS_STATE_SIMPLIFIED_ORDERED } from '@watershed/shared-universal/utils/orderedEnums';
import { CanonicalDatasetGroup } from '@watershed/shared-universal/CanonicalDatasetUtils';

export const UNASSIGNED_USER_ID = 'unassigned'; //  a special value for unassigned tasks
export const WATERSHED_ASSIGNED_USER_ID = 'watershed'; //  a special value for watershed assigned tasks

export const UNTAGGED_TAG_ID = 'untagged'; //  a special value for tasks without any preset tags

export type MeasurementProjectAssigneeFilter =
  | {
      type: 'all';
    }
  | {
      type: 'specific'; // not legal if all users are selected!
      userIds: Array<string>;
    };

export type MeasurementProjectPresetTagFilterTag = {
  id: string;
  value: string;
};

export type MeasurementProjectPresetTagFilter =
  | {
      type: 'all';
      operator: 'or' | 'and';
    }
  | {
      type: 'specific'; // not legal if all tags are selected!
      operator: 'or' | 'and';
      tagIds: Array<MeasurementProjectPresetTagFilterTag>;
    };

export const MEASUREMENT_PROJECT_GROUP_BY_OPTIONS = [
  'dataset',
  'taskStatus',
] as const;
export type MeasurementProjectGroupBy =
  (typeof MEASUREMENT_PROJECT_GROUP_BY_OPTIONS)[number];

export const MEASUREMENT_TASK_STATUSES_SIMPLIFIED = Object.values(
  TASK_WATERSHED_PROCESS_STATE_SIMPLIFIED_ORDERED
);
export const VISIBLE_MEASUREMENT_TASK_STATUSES_SIMPLIFIED =
  MEASUREMENT_TASK_STATUSES_SIMPLIFIED.filter(
    (s) => s !== GQTaskWatershedProcessStateSimplified.None
  );
export type MeasurementTaskStatusSimplified =
  (typeof MEASUREMENT_TASK_STATUSES_SIMPLIFIED)[number];

export interface MeasurementProjectFilters {
  viewType: 'list' | 'board';
  assignees: MeasurementProjectAssigneeFilter;
  statusesSimplified: Array<MeasurementTaskStatusSimplified>;
  presetTags: MeasurementProjectPresetTagFilter;
  groupBy: MeasurementProjectGroupBy;
  search?: string;
}

export const DEFAULT_MEASUREMENT_FILTERS: MeasurementProjectFilters = {
  viewType: 'list',
  assignees: { type: 'all' },
  presetTags: { type: 'all', operator: 'or' },
  statusesSimplified: VISIBLE_MEASUREMENT_TASK_STATUSES_SIMPLIFIED,
  groupBy: 'dataset',
  search: undefined,
};

export type SetMeasurementProjectFilters = (
  filters: MeasurementProjectFilters,
  updateSource: string
) => void;

export function useMeasurementProjectFilters(): readonly [
  MeasurementProjectFilters,
  SetMeasurementProjectFilters,
] {
  const hasManageMeasurement = useHasPermission([
    PermissionType.ManageMeasurement,
  ]);
  const userContext = useUserContext();
  const [filterQuery, setFilterQuery] = useQueryParam('filters');

  const defaultFilters: MeasurementProjectFilters = hasManageMeasurement
    ? DEFAULT_MEASUREMENT_FILTERS
    : {
        ...DEFAULT_MEASUREMENT_FILTERS,
        assignees: { type: 'specific', userIds: [userContext.userId] },
        groupBy: 'taskStatus',
      };

  const jsonFilterQuery = useMemo(() => {
    try {
      return filterQuery ? JSON.parse(filterQuery) : {};
    } catch (err) {
      return {};
    }
  }, [filterQuery]);

  const filters = {
    ...defaultFilters,
    ...jsonFilterQuery,
  };

  const setFiltersWithTracking: SetMeasurementProjectFilters = useCallback(
    (newFilters, updateSource) => {
      Analytics.action('MeasurementProjectFilters', {
        ...newFilters,
        updateSource,
      });
      setFilterQuery(JSON.stringify(newFilters));
    },
    [setFilterQuery]
  );
  return [filters, setFiltersWithTracking] as const;
}

/**
 * If a task has anyone assigned to the task or to approve it return them
 * If a task is assigned to the watershed, return the watershed expert
 */
export function getAssigneesDetailsForTask(
  task: MeasurementTask
): Array<{ name: string; id: string }> {
  if (task.assignees.length + task.approvers.length > 0) {
    return [...task.approvers, ...task.assignees];
  }
  if (task.isAssignedToWatershed) {
    return [
      {
        id: WATERSHED_ASSIGNED_USER_ID,
        name: 'Watershed expert',
      },
    ];
  }
  return [
    {
      id: UNASSIGNED_USER_ID,
      name: 'Unassigned',
    },
  ];
}

export function formatPresetTagsForTask(
  presetTags: Array<GQPresetCustomTagForMeasurementPageFragment>,
  ingestionCustomFields: Array<GQMeasurementProjectPageIngestionCustomFieldFragment>
): Array<{
  id: string;
  tagId: string;
  tagName: string;
  valueName: string;
  type: GQIngestionCustomFieldType;
}> {
  return ingestionCustomFields.map((icf) => {
    const presetTag = presetTags.find(
      (presetTag) => presetTag.ingestionCustomField.id === icf.id
    );
    if (presetTag) {
      return {
        id: `${presetTag.ingestionCustomField.id}__${presetTag.ingestionCustomFieldDatasource.ingestionCustomFieldValue}`,
        tagId: presetTag.ingestionCustomField.id,
        tagName: presetTag.ingestionCustomField.name,
        valueName:
          presetTag.ingestionCustomFieldDatasource.ingestionCustomFieldValue,
        type: presetTag.ingestionCustomField.type,
      };
    } else {
      return {
        id: `${icf.id}__${UNTAGGED_TAG_ID}`,
        tagId: icf.id,
        tagName: icf.name,
        valueName: 'Untagged',
        type: GQIngestionCustomFieldType.String,
      };
    }
  });
}

export function applyFiltersToFlattenedTasks({
  tasks,
  filters,
  taskIdToPresetCustomTags,
  ingestionCustomFields,
  presetCustomTagsForBuildingsEnabled,
}: {
  tasks: ReadonlyArray<MeasurementFlattenedTask>;
  filters: MeasurementProjectFilters;
  taskIdToPresetCustomTags: Map<
    string,
    Array<GQPresetCustomTagForMeasurementPageFragment>
  >;
  ingestionCustomFields: Array<GQMeasurementProjectPageIngestionCustomFieldFragment>;
  presetCustomTagsForBuildingsEnabled?: boolean;
}) {
  let filteredTasks = tasks;

  // Filter by assignee.
  if (filters.assignees.type !== 'all') {
    const userIdSet = new Set(filters.assignees.userIds);
    const assigneeMatches = (task: MeasurementTask) =>
      getAssigneesDetailsForTask(task).some((assignee) =>
        userIdSet.has(assignee.id)
      );

    filteredTasks = filteredTasks.filter((task) => assigneeMatches(task));
  }

  // Filter by simplified status.
  if (
    filters.statusesSimplified.length !==
    VISIBLE_MEASUREMENT_TASK_STATUSES_SIMPLIFIED.length
  ) {
    const statusSet: Set<GQTaskWatershedProcessStateSimplified> = new Set(
      filters.statusesSimplified
    );
    filteredTasks = filteredTasks.filter((task) =>
      statusSet.has(task.statusSimplified)
    );
  }

  // Filter by preset tags.
  // If we are filtering against "all" tags, then use all know ingestion
  // custom fields as the filter tags, otherwise use the tags explicitly set
  // as part of the filter.
  const filterTagIds =
    filters.presetTags.type === 'all'
      ? ingestionCustomFields.map((icf) => ({ id: icf.id, value: '' }))
      : filters.presetTags.tagIds;

  const includeAll =
    filters.presetTags.type === 'all' && filters.presetTags.operator === 'or';

  const presetTagMatches = (task: MeasurementFlattenedTask) => {
    // If we're filtering against "all" tags and using the "or" operator, just
    // return true early, as every task will be included.
    if (includeAll) {
      return true;
    }

    if (
      !presetCustomTagsForBuildingsEnabled &&
      task.taskGroup === CanonicalDatasetGroup.Facilities
    ) {
      return false;
    }

    const formattedTags = formatPresetTagsForTask(
      taskIdToPresetCustomTags.get(task.id) ?? [],
      ingestionCustomFields
    );
    return filters.presetTags.operator === 'and'
      ? filterTagIds.filter(
          (tag) => !!formattedTags.find((f) => f.id === tag.id)
        ).length === filterTagIds.length
      : formattedTags.some((tag) =>
          filterTagIds.find((selectedTagId) => tag.id === selectedTagId.id)
        );
  };

  filteredTasks = filteredTasks.filter((task) => presetTagMatches(task));

  // Filter by search.
  if (filters.search) {
    const search = filters.search.toLowerCase();
    filteredTasks = filteredTasks.filter((task) =>
      task.title.toLowerCase().includes(search)
    );
  }

  return filteredTasks;
}
